import { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Divider,
  List,
  ListItem,
  MenuItem,
  Select,
  Tooltip,
  Typography
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  getAuthenticated,
  isAiAddonLocked,
  isLocalSeoUser,
  sanitizeURL,
  setKeywordResearchMenuStateStorage,
  setOrbitAiMenuStateStorage,
  setOtherToolMenuStateStorage,
  setSiteAuditMenuStateStorage
} from 'src/common/config/FunctionHelper';
import type { RootState } from 'src/redux/store';
import { menu } from './menuList';
import {
  restrictedMenus,
  menuListToShowUpgrade,
  userRoles,
  diyRestrictedMenus,
  paths,
  keywordResearchSubmenuTitles,
  siteAuditMenuHeadings,
  auditSitesDropdownText,
  otherToolMenuHeadings,
  orbitAiMenuHeadings,
  restrictLocalSeoMenus,
  sideBarAllBusinessDashBoardPath,
  sideBarAllBusinessPath,
  menuListToShowUpgradeAfterAiPurchase,
  planNames
} from 'src/common/config/TextMessage';
import { MenuWrapper, SubMenuWrapper, siteAuditStyles } from './sideBarCss';
import { KeyboardArrowUp, KeyboardArrowDown, Add } from '@mui/icons-material';
import {
  setAddedKeywordTrackingData,
  setBlogPostGenerateIdeas,
  setBlogPostSection,
  setBulkKeywordDifficultyReduxData,
  setCompetitorAnalysisSection,
  setCompetitorAnalysisUrl,
  setCompetitorDetailsReduxData,
  setKeywordOverviewFilteredData,
  setKeywordOverviewKeyword,
  setKeywordOverviewSection,
  setKeywordTrackingDetails,
  setRelatedKeywordsReduxData
} from 'src/redux/keywordResearch';
import { auditSite, auditStatus } from 'src/common/Scraper';
import { setIsDeleteSlot } from 'src/redux/userManagementReducer';
import {
  setAuditSiteDetails,
  setIsGSCDisconnected,
  setLoader,
  setProgressLoader,
  setShowSection,
  setSiteAuditDetails,
  setSiteAuditRefresh,
  setSiteId,
  setSiteStatus,
  setWebsiteMonitoringDetails,
  setIsSiteSoftDeleted
} from 'src/redux/siteAuditReducer';
import { siteAuditCSS } from 'src/content/siteAudit/siteAuditCSS';
import UpgradeModalForSiteAudit from 'src/content/siteAudit/UpgradeModalForSiteAudit';
import { setIsDowngradedWithSites } from 'src/redux/userReducer';
import {
  setAiVisibilityGraphData,
  setDashboardDataFilter,
  setGscGraphDetails,
  setGraphFilter,
  setIsGscLoader,
  setIsSiteSelected,
  setOrganicSearchTrafficDetails
} from 'src/redux/dashboard';
import { ListDataTableCss } from 'src/content/keywordResearch/keywordsLists/KeywordListsCss';
import { getFaviconUrl } from 'src/common/config/FunctionHelperPartTwo';

const MenuProps = {
  PaperProps: {
    sx: {
      maxHeight: 300, // Adjust this value as needed
      // overflow: 'auto',
      width: 231,
      top: '133px !important',
      left: '8px !important',
      borderRadius: '6px',
      boxShadow: '0px 0px 12px 2px rgba(0,0,0,0.06)',
      padding: '8px 0px 8px 0px',
      '&::-webkit-scrollbar': {
        width: '8px'
      },
      '&::-webkit-scrollbar-track': {
        background: 'transparent'
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(234, 236, 240, 1)',
        borderRadius: '8px'
      },
      '&::-webkit-scrollbar-thumb:hover': {
        backgroundColor: 'rgba(220, 220, 220, 1)'
      }
    }
  },
  disableScrollLock: true
};

const CustomIcon = () => (
  <img
    src={process.env.PUBLIC_URL + `/static/images/icons/uil_sort.svg`}
    alt="icon"
    style={{ cursor: 'pointer' }}
  />
);

/**
 * React component representing the sidebar menu.
 * This component manages the menu items and their styles.
 */
const SidebarMenu = () => {
  let keywordDropDownState = false;
  let siteAuditDropDownState = false;
  let otherToolDropDownState = false;
  const [auditSiteDetailes, setAuditSiteDetailes] = useState([]);
  const [selectedValue, setSelectedValue] = useState('');
  const [open, setOpen] = useState(false);
  const savedState = localStorage.getItem('keywordResearchMenuState');
  const siteAuditSavedState = localStorage.getItem('siteAuditMenuState');
  const otherToolSavedState = localStorage.getItem('otherToolMenuState');
  const orbitAiSavedState = localStorage.getItem('orbitAiMenuState');
  if (savedState !== null) {
    keywordDropDownState = JSON.parse(savedState);
  }
  if (siteAuditSavedState !== null) {
    siteAuditDropDownState = JSON.parse(siteAuditSavedState);
  }
  if (otherToolSavedState !== null) {
    otherToolDropDownState = JSON.parse(otherToolSavedState);
  }
  let orbitAiDropDownState = false;
  if (orbitAiSavedState !== null) {
    orbitAiDropDownState = JSON.parse(orbitAiSavedState);
  }
  let subscriptionDetails = useSelector(
    (state: RootState) => state.counter?.userDetails['subscription']
  );
  let activePlanName = useSelector((state: RootState) => state.user.activePlan);
  let planDetails = useSelector((state: RootState) => state.user.planDetails);
  let userDetails: any =
    useSelector((state: RootState) => state.counter.userDetails) ?? {};
  const competitorAnalysisSection =
    useSelector(
      (state: RootState) => state.keywordResearch.competitorAnalysisSection
    ) ?? 0;
  const keywordOverviewSection =
    useSelector(
      (state: RootState) => state.keywordResearch.keywordOverviewSection
    ) ?? 0;
  let isPremiumUser = useSelector((state: RootState) => state.user.premiumUser);
  const subscription: any = useSelector(
    (state: RootState) => state.user.subscription
  );
  const { isGSCDisconnected, disconnectedGSCSiteId } = useSelector(
    (state: RootState) => state.siteAudit
  );
  const isNotUnlocked = isAiAddonLocked();
  const isNotPause = subscription && !subscription?.pause_collection?.behavior;

  const isPause = subscription && subscription?.pause_collection?.behavior;
  const isOldPlan = subscription?.planVersion == 'v1'

  let menuList: any = menu(subscriptionDetails);
  if (isPause) {
    menuList = undefined;
  }
  const dispatch = useDispatch();

  const [isKeywordDropdownOpen, setKeywordDropdownOpen] =
    useState(keywordDropDownState);
  const [isSiteAuditOpen, setSiteAuditOpen] = useState(siteAuditDropDownState);
  const [isOtherToolOpen, setOtherToolOpen] = useState(otherToolDropDownState);
  const [isOrbitAiOpen, setOrbitAiOpen] = useState(orbitAiDropDownState);
  const [disableDropdown, setDisableDropdown] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  // State to store Google authentication text.
  const [googleAuthText, setGoogleAuth] = useState(null);

  // User logout
  const navigate = useNavigate();
  let location = useLocation();
  const isLocalSeo = isLocalSeoUser();
  const isDIY = activePlanName === planDetails.diy || activePlanName === planNames.starter || activePlanName === planNames.starterPlus;
  const isMember = userDetails?.role_type === userRoles.member;
  const isDiyOrFreePlan =
    isDIY || activePlanName === planDetails.free;
  const isFreePlan = activePlanName === planDetails.free || isLocalSeo;
  const isEnterprise = activePlanName === planDetails?.enterprise;
  const isAgency =
    activePlanName === planDetails.agency ||
    activePlanName === planDetails.expertPlus;
  let siteAuditScreen =
    useSelector((state: RootState) => state.siteAudit.showSection) ?? 0;
  let siteAuditRefresh = useSelector(
    (state: RootState) => state.siteAudit.siteAuditRefresh
  );
  let sitesDetail =
    useSelector((state: RootState) => state.counter.sitesDetails) ?? {};
  let scanDetailRedux = useSelector(
    (state: RootState) => state.counter.scanDetails
  );
  const isDeleteSlot =
    useSelector((state: RootState) => state.userManagement.isDeleteSlot) ??
    false;
  let isDowngradedWithSites = useSelector(
    (state: RootState) => state.user.isDowngradedWithSites
  );
  let isSiteSelected = useSelector(
    (state: RootState) => state.dashboard.isSiteSelected
  );
  let selectedSiteData: any = useSelector(
    (state: RootState) => state.dashboard.selectedSiteData
  );
  // Selector to retrieve site ID from the Redux store.
  const siteId = useSelector((state: RootState) => state.siteAudit.siteId) ?? 0;

  const updatedShowUpgradeMenuList = isNotUnlocked
    ? menuListToShowUpgrade
    : menuListToShowUpgradeAfterAiPurchase;
  const blogPostSection =
    useSelector((state: RootState) => state.keywordResearch.blogPostSection) ??
    0;
  // Submenu items for Keyword Research
  const keywordResearchSubmenu = [
    {
      title: keywordResearchSubmenuTitles?.keywordOverview,
      url: paths.keywordOverview
    },
    {
      title: keywordResearchSubmenuTitles?.competitorAnalysis,
      url: paths.competitorAnalysis
    },
    {
      title: keywordResearchSubmenuTitles?.keywordTracking,
      url: paths.keywordTracking
    },
    // blog post is disabled
    {
      title: keywordResearchSubmenuTitles?.blogPostIdeas,
      url: paths.blogPostIdeas
    },
    // keyword lists is disabled
    {
      title: keywordResearchSubmenuTitles?.keywordsLists,
      url: paths.keywordsLists
    }
  ];

  const siteAuditSubMenu = [
    {
      title: siteAuditMenuHeadings?.scannedPage,
      url: paths.scannedPages
    },
    {
      title: siteAuditMenuHeadings?.seoTasks,
      url: paths.seoTasks
    },
    {
      title: siteAuditMenuHeadings?.aiVisibility,
      url: paths.aiVisibility
    },
    {
      title: siteAuditMenuHeadings?.externalScan,
      url: paths.externalScans
    }
  ];

  const otherToolsSubMenu = [
    {
      title: otherToolMenuHeadings?.localChecker,
      url: paths.localChecker
    },
    {
      title: otherToolMenuHeadings?.localSEOTasks,
      url: paths.localTasks
    },
    {
      title: otherToolMenuHeadings?.listingManager,
      url: paths.listingManager
    },
    {
      title: otherToolMenuHeadings?.googleReviews,
      url: paths.googleReviews
    },
    {
      title: otherToolMenuHeadings?.businessPosts,
      url: paths.businessPosts
    },
    {
      title: otherToolMenuHeadings?.mapRankTracker,
      url: paths.mapRankTracker
    },
    {
      title: otherToolMenuHeadings?.citationBuilder,
      url: paths.citationBuilder
    }
  ];

  const orbitAiSubMenu = [
    ...(isEnterprise
      ? []
      : [
          {
            title: orbitAiMenuHeadings?.keywordAssist,
            url: paths.keywordAssist,
            isNew: true
          },
          {
            title: orbitAiMenuHeadings?.contentStrategy,
            url: paths.contentStrategy,
            isNew: true
          },
          // Todo: Content Plan - Enable Content Plan sidebar menu link once feature is released
          /*
          {
            title: orbitAiMenuHeadings?.contentPlan,
            url: paths.contentPlan,
            isNew: true
          },
          */
          {
            title: orbitAiMenuHeadings?.blogPostAgent,
            url: paths.blogPostAgent,
            isNew: true
          },
          {
            title: orbitAiMenuHeadings?.localSeoAi,
            url: paths.localSeoAi,
            isNew: true
          },
          {
            title: orbitAiMenuHeadings?.reviewAutomation,
            url: paths.reviewAutomation,
            isNew: true
          }
        ])
  ];

  if (isOldPlan ? ((!isAgency || isLocalSeo) && isNotPause && !isEnterprise) : ((isLocalSeo || isFreePlan) && isNotPause)) {
    menuList = menuList?.filter((item) => item.title !== 'Lead Generation');
    if (isLocalSeo) {
      menuList = menuList?.filter((item) => item.title !== 'Backlink Analysis');
    }
  }

  if (isMember && isNotPause) {
    menuList = menuList?.filter(
      (item) => !restrictedMenus.includes(item.title)
    );
  } else if (isDiyOrFreePlan && isNotPause) {
    menuList = menuList?.filter(
      (item) => !diyRestrictedMenus.includes(item.title)
    );
    if (isFreePlan && isNotPause) {
      if (isNotUnlocked) {
        menuList = menuList?.map((item) => {
          if (item.title === 'External Scans') {
            return { ...item, title: 'Page Scans' };
          }
          return item;
        });
      } else {
        menuList = menuList?.filter(
          (item) =>
            item.title !== 'External Scans' && item.title !== 'AI Visibility'
        );
      }
    }
  }
  if (isLocalSeo) {
    menuList = menuList?.filter(
      (item) => !restrictLocalSeoMenus.includes(item.title)
    );
    menuList = menuList?.map((item) => {
      if (item.title === 'Local SEO') {
        return { ...item, title: 'Overview', icon: '/localseo-overview.svg' };
      }
      return item;
    });
  }

  // Function to get the last part of the URL path
  const getLastPathSegment = (url) => {
    const segments = url.split('/');
    return segments[segments.length - 1];
  };

  const currentPathSegment = getLastPathSegment(location?.pathname);
  useEffect(() => {
    if (!location?.pathname?.includes(paths?.keywordResearch)) {
      setKeywordDropdownOpen(false);
      setKeywordResearchMenuStateStorage(false);
      dispatch(setKeywordOverviewKeyword(null));
      if (location?.pathname !== paths?.backlinkPage) {
        dispatch(setCompetitorAnalysisUrl(null));
      }
    } else {
      // Toggle the dropdown state here
      if (location.pathname !== paths?.keywordResearch) {
        setKeywordDropdownOpen(true);
        setKeywordResearchMenuStateStorage(true);
      }
    }
    if (
      location?.pathname !== paths?.siteAudit &&
      location?.pathname !== paths?.seoTasks &&
      location?.pathname !== paths?.externalScans &&
      location?.pathname !== paths?.scannedPages &&
      location?.pathname !== paths?.aiVisibility
    ) {
      setSiteAuditOpen(false);
      setSiteAuditMenuStateStorage(false);
    } else {
      setSiteAuditOpen(true);
      setSiteAuditMenuStateStorage(true);
    }
    if (!location?.pathname?.includes(paths?.otherTools)) {
      setOtherToolOpen(false);
      setOtherToolMenuStateStorage(false);
    } else {
      if (location.pathname !== paths?.otherTools) {
        setOtherToolOpen(true);
        setOtherToolMenuStateStorage(true);
      }
    }
    if (!location?.pathname?.includes(paths?.orbitAiAgent)) {
      setOrbitAiOpen(false);
      setOrbitAiMenuStateStorage(false);
    } else {
      if (location.pathname !== paths?.orbitAiAgent) {
        setOrbitAiOpen(true);
        setOrbitAiMenuStateStorage(true);
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    if (
      location.pathname === paths.siteAudit ||
      location.pathname === paths.seoTasks ||
      location.pathname.includes(paths.keywordResearch) ||
      location.pathname === paths.dashboard ||
      location.pathname === paths.scannedPages ||
      location.pathname.includes(paths.otherTools) ||
      location.pathname.includes(paths.orbitAiAgent) ||
      location.pathname === paths.viewAllSites
    ) {
      setDisableDropdown(false);
    } else {
      setDisableDropdown(true);
    }

    return () => {
      setDisableDropdown(false);
    };
  }, [location.pathname]);

  const handleCloseUpgradeModal = () => {
    setShowUpgradeModal(false);
  };

  useEffect(() => {
    if (auditSiteDetailes?.length == 0 || !auditSiteDetailes?.length) {
      dispatch(setSiteStatus(false));
    }
    if (auditSiteDetailes?.length) {
      let getLastSelectedSite = localStorage.getItem('lastSelectedSite');
      let selectedSiteAuditDetails = auditSiteDetailes[0];

      if (getLastSelectedSite != null) {
        let lastSelectedSite = JSON.parse(getLastSelectedSite);
        if (
          lastSelectedSite.length > 0 &&
          auditSiteDetailes.find((item) => item._id === lastSelectedSite[1])
        ) {
          setSelectedValue(
            sanitizeURL(lastSelectedSite[0]) + '&&' + lastSelectedSite[1]
          );
          dispatch(setLoader(true));
          checkAuditStatus(lastSelectedSite[1]);
          dispatch(setIsSiteSoftDeleted(isSiteIsSoftDeleted(lastSelectedSite[1])));
        } else {
          setSelectedValue(
            sanitizeURL(selectedSiteAuditDetails.url) +
              '&&' +
              selectedSiteAuditDetails._id
          );
          dispatch(setLoader(true));
          checkAuditStatus(selectedSiteAuditDetails._id);
          dispatch(setIsSiteSoftDeleted(isSiteIsSoftDeleted(selectedSiteAuditDetails._id)));
          let values = [
            selectedSiteAuditDetails.url,
            selectedSiteAuditDetails._id
          ];
          localStorage.setItem('lastSelectedSite', JSON.stringify(values));
        }
      } else {
        setSelectedValue(
          sanitizeURL(selectedSiteAuditDetails.url) +
            '&&' +
            selectedSiteAuditDetails._id
        );
        dispatch(setLoader(true));
        checkAuditStatus(selectedSiteAuditDetails._id);
        dispatch(setIsSiteSoftDeleted(isSiteIsSoftDeleted(selectedSiteAuditDetails._id)));
        let values = [
          selectedSiteAuditDetails.url,
          selectedSiteAuditDetails._id
        ];
        localStorage.setItem('lastSelectedSite', JSON.stringify(values));
      }
    }
  }, [auditSiteDetailes?.length]);

  useEffect(() => {
    if (isSiteSelected) {
      handleSelectedSite();
    }
  }, [isSiteSelected]);

  const handleSelectedSite = () => {
    let values = [selectedSiteData?.url, selectedSiteData?._id];
    localStorage.setItem('lastSelectedSite', JSON.stringify(values));
    dispatch(setAddedKeywordTrackingData([]));
    dispatch(setKeywordTrackingDetails(null));
    dispatch(setLoader(true));
    dispatch(setGraphFilter('last_7_days'));
    dispatch(setDashboardDataFilter({ startDate: null, endDate: null }));
    setSelectedValue(
      sanitizeURL(selectedSiteData?.url) + '&&' + selectedSiteData?._id
    );
    dispatch(setSiteId(values[1]));
    dispatch(setIsSiteSoftDeleted(isSiteIsSoftDeleted(values[1])));
    dispatch(setProgressLoader(true));
    checkAuditStatus(values[1]);
    navigate(paths?.dashboard);
    dispatch(setIsSiteSelected(false));
  };

  const clearDashboardStates = () => {
    dispatch(setOrganicSearchTrafficDetails(null));
    dispatch(setGscGraphDetails(null));
    dispatch(setWebsiteMonitoringDetails(null));
    dispatch(setAiVisibilityGraphData(null));
    dispatch(setGraphFilter('last_7_days'));
    dispatch(setDashboardDataFilter({ startDate: null, endDate: null }));
  };

  const isSiteIsSoftDeleted = (siteId: string): boolean => {
    const site= auditSiteDetailes.find((site)=>site._id === siteId);
    return site?.isDelete || false;
  }
  /**
   * Handles the change of the selected audit site.
   *
   * @param {object} event - The event object.
   */
  const handleChange = (event: any) => {
    let values = event.target.value.split('&&');

    localStorage.setItem('lastSelectedSite', JSON.stringify(values));
    dispatch(setLoader(true));
    dispatch(setProgressLoader(true));
    clearDashboardStates();
    if (location?.pathname == paths?.blogPostIdeas && blogPostSection !== 0) {
      dispatch(setBlogPostGenerateIdeas(null));
      dispatch(setBlogPostSection(0));
    }
    if (
      location?.pathname == paths?.keywordOverview &&
      keywordOverviewSection !== 0
    ) {
      dispatch(setBulkKeywordDifficultyReduxData(null));
      dispatch(setRelatedKeywordsReduxData(null));
      dispatch(setKeywordOverviewFilteredData(null));
      dispatch(setKeywordOverviewSection(0));
    }
    if (
      location?.pathname == paths?.competitorAnalysis &&
      competitorAnalysisSection !== 0
    ) {
      dispatch(setCompetitorAnalysisSection(0));
      dispatch(setCompetitorDetailsReduxData(null));
    }
    dispatch(setAddedKeywordTrackingData([]));
    dispatch(setKeywordTrackingDetails(null));
    setSelectedValue(event.target.value);
    dispatch(setSiteId(values[1]));
    dispatch(setIsSiteSoftDeleted(isSiteIsSoftDeleted(values[1])));
    checkAuditStatus(values[1]);
  };

  /**
   * Checks the audit status of a site.
   *
   * @param {string} siteid - The ID of the site to check.
   */
  const checkAuditStatus = async (siteid) => {
    let AuditStatusResponse: any = await auditStatus(siteid);
    const item = auditSiteDetailes.find((data) => data['_id'] === siteid);
    let mergedData = { ...AuditStatusResponse, ...item };
    dispatch(setSiteAuditDetails(mergedData));
    dispatch(setSiteId(siteid));
    dispatch(setSiteStatus(AuditStatusResponse?.scan?.processing));
    if (AuditStatusResponse?.scan?.processing === false) {
      dispatch(setLoader(false));
      dispatch(setShowSection(2));
    } else if (AuditStatusResponse?.status === 'success') {
      dispatch(setLoader(false));
      dispatch(setShowSection(1));
    } else {
      dispatch(setLoader(false));
    }
    dispatch(setProgressLoader(false));
  };

  const handleKeywordSearchClick = () => {
    navigate(paths?.keywordResearch);
    // Toggle the dropdown state here
    setKeywordDropdownOpen(!isKeywordDropdownOpen);
    setKeywordResearchMenuStateStorage(!isKeywordDropdownOpen);
  };

  const handleOtherToolClick = () => {
    navigate(paths?.otherTools);
    // Toggle the dropdown state here
    setOtherToolOpen(!isOtherToolOpen);
    setOtherToolMenuStateStorage(!isKeywordDropdownOpen);
  };

  const handleOrbitAiClick = () => {
    navigate(paths?.orbitAiAgent);
    // Toggle the dropdown state here
    setOrbitAiOpen(!isOrbitAiOpen);
    setOrbitAiMenuStateStorage(!isOrbitAiOpen);
  };

  const handleSiteAuditClick = () => {
    if (location.pathname !== paths.siteAudit) {
      navigate(paths?.siteAudit);
    }
    setSiteAuditOpen(!isSiteAuditOpen);
    setSiteAuditMenuStateStorage(!isSiteAuditOpen);
  };

  useEffect(() => {
    (async () => {
      const isAuthenticated: any = getAuthenticated();
      if (isAuthenticated !== 'false') {
        setAudiSiteList();
      }
    })();

    return () => {};
  }, []);

  useEffect(() => {
    (async () => {
      if (siteAuditRefresh) {
        setAudiSiteList();
        dispatch(setSiteAuditRefresh(false));
      }
    })();

    return () => {};
  }, [sitesDetail['sites'], siteAuditRefresh]);

  useEffect(() => {
    (async () => {
      if (isDowngradedWithSites) {
        setAudiSiteList();
        dispatch(setIsDowngradedWithSites(null));
      }
    })();
    return () => {};
  }, [isDowngradedWithSites]);

  useEffect(() => {
    if (isDeleteSlot) {
      setAudiSiteList();
      dispatch(setIsDeleteSlot(false));
    }
    return () => {};
  }, [isDeleteSlot]);

  useEffect(() => {
    // Create a BroadcastChannel for communication with other parts of the application.
    const seoSpaceChannel = new BroadcastChannel('SEO_Space');
    seoSpaceChannel.onmessage = (event) => {
      if (event?.data?.type === 'gsc_auth_connection') {
        // Handle Google Search Console authentication response.
        setGoogleAuth(event.data);
        // Clear the Google authentication message after 5 seconds.
        setTimeout(() => {
          setGoogleAuth(null);
        }, 5000);
      }
    };
    return () => {
      setGoogleAuth(null);
    };
  }, []);

  useEffect(() => {
    // Fetch monthly traffic data and keyword count data when Google authentication status changes.
    if (googleAuthText?.success) {
      dispatch(setIsGscLoader(true));
      bothSiteApis(siteId);
    }
    return () => {};
  }, [googleAuthText]);

  useEffect(() => {
    if (isGSCDisconnected && disconnectedGSCSiteId) {
      bothSiteApis(disconnectedGSCSiteId);
    }
    return () => {
      dispatch(
        setIsGSCDisconnected({
          isGSCDisconnected: false,
          siteId: false
        })
      );
    };
  }, [isGSCDisconnected, disconnectedGSCSiteId]);

  const bothSiteApis = async (siteId: string) => {
    await setAudiSiteList();
    await checkAuditStatus(siteId);
  };

  /**
   * Fetches and sets the list of audit sites.
   */
  const setAudiSiteList = async () => {
    let auditSiteResponse: any = await auditSite();
    if (auditSiteResponse?.length) {
      setAuditSiteDetailes(auditSiteResponse);
      dispatch(setAuditSiteDetails(auditSiteResponse));
    } else {
      setAuditSiteDetailes([]);
      dispatch(setAuditSiteDetails([]));
      if (siteAuditScreen != 0) {
        dispatch(setShowSection(0));
      }
    }
    dispatch(setLoader(false));
  };
  const openModal = () => {
    if (isFreePlan && isNotUnlocked) {
      navigate(paths?.siteAudit);
    } else {
      if (
        parseInt(scanDetailRedux?.['useScan'], 10) >=
        parseInt(scanDetailRedux?.['scan'])
      ) {
        setShowUpgradeModal(true);
      } else {
        // setOpenAddScan(true);
        navigate(isLocalSeo ? paths?.localChecker : paths?.onBoarding);
      }
    }
  };

  const handleKeywordSubMenuClick = (submenuItem) => {
    if (
      location?.pathname == paths?.keywordOverview &&
      keywordOverviewSection == 1
    ) {
      dispatch(setKeywordOverviewSection(0));
    }
    if (
      location?.pathname == paths?.competitorAnalysis &&
      competitorAnalysisSection == 1
    ) {
      dispatch(setCompetitorAnalysisSection(0));
    }
  };

  const handleOpen = () => {
    if (!open) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  };
  return (
    <>
      <MenuWrapper>
        <List component="div">
          <SubMenuWrapper>
            <List component="div">
              {/* Dynamic menu item  button */}
              {menuList === undefined ? (
                ''
              ) : (
                <div className={isLocalSeo ? 'pt-6' : ''}>
                  {!isLocalSeo && (
                    <Box
                      className="pt-6 px-2"
                      sx={siteAuditStyles.sidebarMenuBox}
                    >
                      {Object.values(auditSiteDetailes)?.length !== 0 ? (
                        <div
                          className="relative w-min lg:max-w-sm"
                          style={{
                            display: 'flex',
                            cursor: 'pointer !important'
                          }}
                          onClick={handleOpen}
                        >
                          <Select
                            labelId="select"
                            id="select"
                            value={selectedValue}
                            onChange={handleChange}
                            variant="standard"
                            disabled={disableDropdown || isSiteIsSoftDeleted(siteId)}
                            IconComponent={CustomIcon}
                            sx={siteAuditStyles.selectDropdown}
                            disableUnderline={true}
                            MenuProps={MenuProps}
                            open={open} // Control open state
                          >
                            {auditSiteDetailes?.map((item, index) => (
                              <MenuItem
                                key={index}
                                value={
                                  sanitizeURL(item?.url) + '&&' + item?._id
                                }
                                sx={{
                                  ...siteAuditStyles.menuItem,
                                  ...(item?.isDelete && {
                                    opacity: 0.5,
                                    cursor: 'pointer'
                                  })
                                }}
                              >
                                <Avatar
                                  className="site-audit-drop-down"
                                  src={getFaviconUrl(item?.url || '')}
                                  sx={siteAuditCSS.dropDownItemImage}
                                />
                                <Tooltip
                                  title={
                                    <div className="flex items-center">
                                      <p
                                        style={{
                                          color: 'white',
                                          fontSize: '12px'
                                        }}
                                      >
                                        {sanitizeURL(item.url)}
                                      </p>
                                    </div>
                                  }
                                  placement="right"
                                >
                                  <Typography
                                    sx={siteAuditStyles.typographyCss}
                                  >
                                    {sanitizeURL(item.url)}
                                  </Typography>
                                </Tooltip>
                              </MenuItem>
                            ))}
                            <Box sx={siteAuditStyles.stickyBox}>
                              {isEnterprise ? (
                                <Box sx={siteAuditStyles?.viewAllSiteBtnBox}>
                                  <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={openModal}
                                    startIcon={
                                      <Add sx={ListDataTableCss?.addIcon} />
                                    }
                                    sx={siteAuditStyles?.auditNewSiteBtn}
                                  >
                                    {auditSitesDropdownText?.addWebsites}
                                  </Button>
                                  <Button
                                    fullWidth
                                    variant="outlined"
                                    sx={siteAuditStyles?.viewAllSitesBtn}
                                    onClick={() => {
                                      navigate(paths?.viewAllSites);
                                    }}
                                  >
                                    {`${
                                      auditSitesDropdownText?.viewAllWebsites
                                    } (${auditSiteDetailes?.length || 0})`}
                                  </Button>
                                </Box>
                              ) : (
                                <>
                                  <Divider />
                                  {(isPremiumUser ||
                                    (isFreePlan && !isNotUnlocked)) && (
                                    <MenuItem
                                      sx={siteAuditStyles.auditNewSiteMenu}
                                      onClick={openModal}
                                      className="test"
                                    >
                                      <Button
                                        disabled={disableDropdown}
                                        variant="text"
                                        startIcon={
                                          <Add
                                            sx={
                                              siteAuditStyles.auditNewSiteBtnIcon
                                            }
                                          />
                                        }
                                        onClick={openModal}
                                        sx={siteAuditStyles.button}
                                      >
                                        <Typography
                                          sx={siteAuditStyles.buttonText}
                                        >
                                          {auditSitesDropdownText?.auditNewSite}
                                        </Typography>
                                      </Button>
                                    </MenuItem>
                                  )}
                                </>
                              )}
                            </Box>
                          </Select>
                        </div>
                      ) : (
                        <Box
                          sx={{
                            ...siteAuditStyles.stickyBox,
                            ...siteAuditStyles?.auditNewSiteBtnBoxSx
                          }}
                        >
                          {!isFreePlan && <Divider />}
                          {(isPremiumUser || isFreePlan) && (
                            <MenuItem
                              sx={siteAuditStyles.premiumOrFreeUserMenuItem}
                              onClick={openModal}
                            >
                              <Box
                                sx={
                                  siteAuditStyles.premiumOrFreeUserMenuItemBox
                                }
                              >
                                <Box
                                  sx={siteAuditStyles.premiumOrFreeUserMenuItemSubBox(
                                    isFreePlan
                                  )}
                                >
                                  <img
                                    src={
                                      process.env.PUBLIC_URL +
                                      `/static/images/icons/language-svg.svg`
                                    }
                                    alt="default web icon"
                                    width={16}
                                    height={16}
                                    style={{ marginLeft: '-4px' }}
                                  />
                                  <Button
                                    disabled={disableDropdown}
                                    variant="text"
                                    onClick={openModal}
                                    sx={siteAuditStyles.button}
                                  >
                                    <Typography
                                      sx={siteAuditStyles.auditNewSiteBtnText}
                                    >
                                      {isFreePlan && isNotUnlocked
                                        ? auditSitesDropdownText?.addSiteToAnalyze
                                        : auditSitesDropdownText?.auditNewSite}
                                    </Typography>
                                  </Button>
                                </Box>
                                <Box>
                                  {isFreePlan && isNotUnlocked ? (
                                    <Box
                                      sx={{
                                        ...siteAuditStyles.imgBoxSx,
                                        width: '100%',
                                        marginRight: '-16%'
                                        //marginLeft: '4px'
                                      }}
                                    >
                                      <img
                                        draggable={false}
                                        src={
                                          process.env.PUBLIC_URL +
                                          `/static/images/icons/white_lock.svg`
                                        }
                                        alt="lock Icon"
                                      />
                                    </Box>
                                  ) : (
                                    <Add sx={siteAuditStyles.addBtn} />
                                  )}
                                </Box>
                              </Box>
                            </MenuItem>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}

                  {menuList?.map((menu, index) => {
                    if (menu.title === '') {
                      return (
                        <Divider
                          sx={siteAuditStyles.menuItemDivider}
                          key={index}
                        />
                      );
                    }

                    if (menu.title === 'Keyword Research' && !isFreePlan) {
                      return (
                        <div key={index}>
                          <ListItem component="div">
                            <Button
                              onClick={() => {
                                if (menu.title === 'Keyword Research') {
                                  handleKeywordSearchClick();
                                }
                              }}
                              startIcon={
                                <img
                                  src={`/static/images/${'newSidebarIcon'}${
                                    menu.icon
                                  }`}
                                ></img>
                              }
                              // Inside the Button component's endIcon property
                              endIcon={
                                // Use a conditional rendering to show different icons based on dropdown state
                                isKeywordDropdownOpen ? (
                                  <KeyboardArrowUp />
                                ) : (
                                  <KeyboardArrowDown />
                                )
                              }
                              sx={{
                                fontFamily: 'Inter',
                                fontSize: 'var(--font-size-14)',
                                fontWeight: '500 !important'
                              }}
                              className={
                                menu.url == window.location.pathname
                                  ? 'active'
                                  : ''
                              }
                            >
                              {menu.title}
                            </Button>
                          </ListItem>
                          {isKeywordDropdownOpen && (
                            <List>
                              {keywordResearchSubmenu.map(
                                (submenuItem, subIndex) => (
                                  <ListItem key={subIndex} component="div">
                                    <Button
                                      component={Link}
                                      to={submenuItem.url}
                                      onClick={() => {
                                        handleKeywordSubMenuClick(submenuItem);
                                      }}
                                      sx={siteAuditStyles.menuKeywordCss}
                                      className={
                                        getLastPathSegment(submenuItem?.url) ===
                                        currentPathSegment
                                          ? 'active'
                                          : ''
                                      }
                                    >
                                      {submenuItem.title}
                                      {/* {submenuItem?.isNew && (
                                          <div className='ml-auto mr-1 rounded-full border-[5px] border-[#5BDEC440] animate-pulse'>
                                            <div className='w-2 h-2 rounded-full bg-[var(--color-secondary)]' />
                                          </div>
                                      )} */}
                                    </Button>
                                  </ListItem>
                                )
                              )}
                            </List>
                          )}
                        </div>
                      );
                    }
                    if (menu.title === 'Orbit AI' && !isFreePlan) {
                      return (
                        <div key={index}>
                          <ListItem component="div">
                            <Button
                              onClick={() => {
                                if (menu.title === 'Orbit AI') {
                                  handleOrbitAiClick();
                                }
                              }}
                              startIcon={
                                <img
                                  src={`/static/images/${'newSidebarIcon'}${
                                    menu.icon
                                  }`}
                                ></img>
                              }
                              // Inside the Button component's endIcon property
                              endIcon={
                                // Use a conditional rendering to show different icons based on dropdown state
                                isOrbitAiOpen ? (
                                  <KeyboardArrowUp />
                                ) : (
                                  <KeyboardArrowDown />
                                )
                              }
                              sx={{
                                fontFamily: 'Inter',
                                fontSize: 'var(--font-size-14)',
                                fontWeight: '500 !important'
                              }}
                              className={
                                menu.url == window.location.pathname
                                  ? 'active'
                                  : ''
                              }
                            >
                              {menu.title}
                            </Button>
                          </ListItem>
                          {isOrbitAiOpen && (
                            <List>
                              {orbitAiSubMenu.map(
                                (submenuItem, subIndex) => (
                                  <ListItem key={subIndex} component="div">
                                    <Button
                                      component={Link}
                                      to={submenuItem.url}
                                      sx={siteAuditStyles.menuKeywordCss}
                                      className={
                                        getLastPathSegment(submenuItem?.url) ===
                                        currentPathSegment
                                          ? 'active'
                                          : ''
                                      }
                                    >
                                      {submenuItem.title}
                                      {submenuItem?.isNew && (
                                          <div className='ml-auto mr-1 rounded-full border-[5px] border-[#5BDEC440] animate-pulse'>
                                            <div className='w-2 h-2 rounded-full bg-[var(--color-secondary)]' />
                                          </div>
                                      )}
                                    </Button>
                                  </ListItem>
                                )
                              )}
                            </List>
                          )}
                        </div>
                      );
                    }
                    if (
                      menu.title === 'Site Audit' &&
                      (!isFreePlan || (isFreePlan && !isNotUnlocked))
                    ) {
                      return (
                        <div key={index}>
                          <ListItem component="div">
                            <Button
                              startIcon={
                                <img
                                  src={`/static/images/${'newSidebarIcon'}${
                                    menu.icon
                                  }`}
                                ></img>
                              }
                              // Inside the Button component's endIcon property
                              endIcon={
                                // Use a conditional rendering to show different icons based on dropdown state
                                isSiteAuditOpen ? (
                                  <KeyboardArrowUp />
                                ) : (
                                  <KeyboardArrowDown />
                                )
                              }
                              sx={siteAuditStyles.menuTitleBtn}
                              className={
                                menu.url == window.location.pathname
                                  ? 'active'
                                  : ''
                              }
                              onClick={() => {
                                if (menu.title === 'Site Audit') {
                                  handleSiteAuditClick();
                                }
                              }}
                            >
                              {menu.title}
                            </Button>
                          </ListItem>
                          {isSiteAuditOpen && (
                            <List>
                              {siteAuditSubMenu.map((submenuItem, subIndex) => (
                                <ListItem
                                  key={subIndex}
                                  component="div"
                                  sx={{
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}
                                >
                                  <Button
                                    component={Link} // Tell the Button component to use Link as the underlying component
                                    to={submenuItem.url} // Use 'to' instead of 'href'
                                    sx={siteAuditStyles.menuKeywordCss}
                                    className={
                                      getLastPathSegment(submenuItem?.url) ===
                                      currentPathSegment
                                        ? 'active'
                                        : ''
                                    }
                                  >
                                    {submenuItem.title}
                                  </Button>
                                </ListItem>
                              ))}
                            </List>
                          )}
                        </div>
                      );
                    }
                    if (menu.title === 'Local SEO' && !isFreePlan) {
                      return (
                        <div key={index}>
                          <ListItem component="div">
                            <Button
                              onClick={() => {
                                if (menu.title === 'Local SEO') {
                                  handleOtherToolClick();
                                }
                              }}
                              startIcon={
                                <img
                                  src={`/static/images/${'newSidebarIcon'}${
                                    menu.icon
                                  }`}
                                ></img>
                              }
                              // Inside the Button component's endIcon property
                              endIcon={
                                // Use a conditional rendering to show different icons based on dropdown state
                                isOtherToolOpen ? (
                                  <KeyboardArrowUp />
                                ) : (
                                  <KeyboardArrowDown />
                                )
                              }
                              sx={{
                                fontFamily: 'Inter',
                                fontSize: 'var(--font-size-14)',
                                fontWeight: '500 !important'
                              }}
                              className={
                                menu.url == window.location.pathname
                                  ? 'active'
                                  : ''
                              }
                            >
                              {menu.title}
                            </Button>
                          </ListItem>
                          {isOtherToolOpen && (
                            <List>
                              {otherToolsSubMenu.map(
                                (submenuItem, subIndex) => (
                                  <ListItem key={subIndex} component="div">
                                    <Button
                                      component={Link}
                                      to={submenuItem.url}
                                      sx={siteAuditStyles.menuKeywordCss}
                                      className={
                                        getLastPathSegment(submenuItem?.url) ===
                                          currentPathSegment ||
                                        (getLastPathSegment(
                                          submenuItem?.url
                                        ) === sideBarAllBusinessDashBoardPath &&
                                          sideBarAllBusinessPath ===
                                            currentPathSegment) ||
                                        (submenuItem?.url ===
                                          paths.citationBuilder &&
                                          currentPathSegment ===
                                            'citation-waiting-list')
                                          ? 'active'
                                          : ''
                                      }
                                    >
                                      {submenuItem.title}
                                      {/* {submenuItem?.isNew && (
                                        <div className='ml-auto mr-1 rounded-full border-[5px] border-[#5BDEC440] animate-pulse'>
                                          <div className='w-2 h-2 rounded-full bg-[var(--color-secondary)]' />
                                        </div>
                                      )} */}
                                    </Button>
                                  </ListItem>
                                )
                              )}
                            </List>
                          )}
                        </div>
                      );
                    }
                    return (
                      <ListItem
                        key={index}
                        component="div"
                        className={[
                          menu.classOption === 'topOption' ? 'topOption' : ''
                        ].join(' ')}
                      >
                        <Button
                          onClick={() => {
                            navigate(menu.url);
                          }}
                          startIcon={
                            <img
                              src={`/static/images/${'newSidebarIcon'}${
                                menu.icon
                              }`}
                            ></img>
                          }
                          endIcon={
                            menu.endIcon && (
                              <img
                                src={`/static/images/${'newSidebarIcon'}${
                                  isLocalSeo ? menu.endIcon : menu.icon
                                }`}
                              ></img>
                            )
                          }
                          sx={
                            menu?.isTrySeo
                              ? {
                                  background:
                                    'linear-gradient(121.16deg, var(--color-purple-alt) 12.89%, #1E0079 54%, var(--color-purple-alt) 84.37%);',
                                  ...siteAuditStyles.listItemButtonCss,
                                  '&:hover': {
                                    background:
                                      'linear-gradient(121.16deg, var(--color-purple-alt) 12.89%, #1E0079 54%, var(--color-purple-alt) 84.37%);'
                                  }
                                }
                              : siteAuditStyles.listItemButtonCss
                          }
                          className={[
                            menu.url == window.location.pathname ||
                            (menu.url == paths?.dashboard &&
                              window.location.pathname == paths?.viewAllSites)
                              ? 'active'
                              : ''
                          ].join(' ')}
                        >
                          {menu.title}&nbsp;
                          {updatedShowUpgradeMenuList?.includes(menu?.title) &&
                            (!isPremiumUser || isLocalSeo) && (
                              <Box sx={siteAuditStyles.imgBoxSx}>
                                <img
                                  draggable={false}
                                  src={
                                    process.env.PUBLIC_URL +
                                    `/static/images/icons/white_lock.svg`
                                  }
                                  alt="lock Icon"
                                />
                              </Box>
                            )}
                        </Button>
                      </ListItem>
                    );
                  })}
                </div>
              )}
            </List>
          </SubMenuWrapper>
        </List>
      </MenuWrapper>

      <UpgradeModalForSiteAudit
        showUpgradeModal={showUpgradeModal}
        setShowUpgradeModal={setShowUpgradeModal}
        handleCloseUpgradeModal={handleCloseUpgradeModal}
      />
    </>
  );
};

export default SidebarMenu;
